"use client";

import { Children, cloneElement, forwardRef, isValidElement, useEffect, useId, useRef, type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { useEscapeStack } from "./overlayStack";
import { useAnchoredPosition, type Align, type Side } from "./floating";
import { useControlledState, useIsClient } from "./hooks";

// Phase 4.4 / 4.5 rewrite three things about this component; the prop shape
// is unchanged apart from two additions, so existing call sites keep working.
//
// 1. CLIPPING (F15). The panel was absolutely positioned inside the trigger's
//    containing block, so a popover opened from a toolbar inside a scrolling
//    table was cut off. It now renders through a portal at fixed coordinates
//    with automatic flipping — see floating.ts.
//
// 2. NO ACCESSIBLE NAME (F16). It declared `role="dialog"` with nothing to
//    name it, so a screen reader announced "dialog" and stopped. A dialog
//    role without a name is worse than no role at all. `label` is now a
//    required-in-practice prop: pass visible heading text via `aria-label`
//    or point `aria-labelledby` at your own heading. When neither is given
//    the component falls back to `role="group"`, which is honest about what
//    it is rather than claiming a dialog it cannot name.
//
// 3. FOCUS DID NOT MOVE IN (F16). Opening a popover left focus on the
//    trigger, so keyboard and screen-reader users could tab straight past
//    the content they had just opened. Focus now moves to the first
//    focusable element inside (or the panel itself), and returns to the
//    trigger on close.
//
// Escape now goes through the shared overlay stack, so a popover opened over
// a modal closes only itself.
export type PopoverSide = Side;
export type PopoverAlign = Align;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface PopoverProps extends Omit<HTMLAttributes<HTMLDivElement>, "content" | "onChange"> {
  content: ReactNode;
  children: ReactElement;
  side?: PopoverSide;
  align?: PopoverAlign;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Closes on a click outside the trigger+panel. Default true. */
  closeOnOutsideClick?: boolean;
  /** Moves focus into the panel on open. Default true; turn off only for a
   *  panel whose content is purely informational. */
  autoFocus?: boolean;
  /** Match the trigger's width — the right default for select-style menus. */
  matchTriggerWidth?: boolean;
}

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(function Popover(
  {
    content,
    children,
    side = "bottom",
    align = "start",
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    closeOnOutsideClick = true,
    autoFocus = true,
    matchTriggerWidth = false,
    className,
    id: providedId,
    ...rest
  },
  ref
) {
  const fallbackId = useId();
  const popoverId = providedId ?? fallbackId;
  const [openState, setOpenState] = useControlledState<boolean>({
    ...(openProp !== undefined ? { value: openProp } : {}),
    defaultValue: defaultOpen,
    ...(onOpenChange ? { onChange: onOpenChange } : {}),
  });
  const open = openState ?? false;
  // Portals need a DOM target, which does not exist during the server render
  // — reachable here because `defaultOpen` can make the very first render open.
  const mounted = useIsClient();

  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const lastActiveRef = useRef<HTMLElement | null>(null);

  const child = Children.only(children);
  if (!isValidElement(child)) {
    throw new Error("Popover requires a single React element trigger child.");
  }

  function setOpen(next: boolean) {
    if (next) lastActiveRef.current = (document.activeElement as HTMLElement) ?? null;
    setOpenState(next);
    if (!next) lastActiveRef.current?.focus();
  }

  useEscapeStack(open, () => setOpen(false));

  // Outside click. Checks BOTH the trigger wrapper and the portalled panel,
  // since the panel is no longer a DOM descendant of the trigger — without
  // the second test, clicking inside your own popover would close it.
  useEffect(() => {
    if (!open || !closeOnOutsideClick) return;
    function onDocMouseDown(e: MouseEvent) {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpenState(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closeOnOutsideClick]);

  const pos = useAnchoredPosition({ open, anchorRef, panelRef, side, align, offset: 6, matchTriggerWidth });

  // d2d-DetectHub eval 4.2: the panel is visibility:hidden until its first
  // position measurement (data-open), and focus() on a hidden element does
  // nothing, so focus moved in only when the measurement happened to win the
  // race. Wait for it.
  useEffect(() => {
    if (!open || !autoFocus || !pos.ready) return;
    const panel = panelRef.current;
    if (!panel) return;
    const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (first ?? panel).focus({ preventScroll: true });
  }, [open, autoFocus, pos.ready]);

  const { onClick: triggerOnClick } = child.props as { onClick?: (e: React.MouseEvent) => void };

  // A `dialog` role is only claimed when the panel can actually be named.
  const named = Boolean(rest["aria-label"] || rest["aria-labelledby"]);

  // d2d-DetectHub eval F16: the trigger said aria-haspopup="dialog" even when
  // the panel rendered as role="group". It now says so only for a dialog.
  const enhanced = cloneElement(child, {
    // A trigger that says what it opens (a menu button's "menu") keeps it.
    "aria-haspopup": named ? "dialog" : (child.props as { "aria-haspopup"?: unknown })["aria-haspopup"],
    "aria-expanded": open,
    "aria-controls": open ? popoverId : undefined,
    onClick: (e: React.MouseEvent) => {
      triggerOnClick?.(e);
      setOpen(!open);
    },
  } as Record<string, unknown>);

  const panel = open && mounted ? createPortal(
    <div
      ref={(node) => {
        panelRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      id={popoverId}
      role={named ? "dialog" : "group"}
      tabIndex={-1}
      data-side={pos.side}
      data-align={align}
      data-open={pos.ready}
      className={cn("rc-popover", className)}
      style={{ left: pos.x, top: pos.y, ...(pos.minWidth ? { minWidth: pos.minWidth } : {}) }}
      {...rest}
    >
      {content}
    </div>,
    document.body
  ) : null;

  return (
    <span ref={anchorRef} className="rc-popover-wrap">
      {enhanced}
      {panel}
    </span>
  );
});
